import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "../brand/Logo";
import { NAV } from "../../config/site";
import { scrollToId } from "../../lib/api";
import { useUI } from "../../context/UIContext";

function useActiveSection() {
  const [active, setActive] = useState("home");
  useEffect(() => {
    const els = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return active;
}

export const Navbar = () => {
  const { openEnquiry } = useUI();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop(); else window.__lenis?.start();
  }, [open]);

  const go = (id) => { setOpen(false); setTimeout(() => scrollToId(id), open ? 250 : 0); };

  return (
    <header data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-700 ${scrolled ? "bg-[#21170F]/85 backdrop-blur-xl border-b border-[#B18A4A]/20 py-3" : "border-b border-transparent py-5 lg:py-7"}`}>
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
        <button type="button" onClick={() => go("home")} data-testid="nav-logo" aria-label="TOYAM home"><Logo tone="ivory" /></button>
        <nav aria-label="Primary" className="hidden xl:block">
          <ul className="flex items-center gap-8 xl:gap-10">
            {NAV.map((n) => (
              <li key={n.id}>
                <button type="button" onClick={() => go(n.id)} data-testid={`nav-link-${n.id}`} data-active={active === n.id}
                  aria-current={active === n.id ? "true" : undefined}
                  className={`link-lux text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-500 ${active === n.id ? "text-[#E8D6B7]" : "text-[#F8F3E9]/80 hover:text-[#F8F3E9]"}`}>
                  {n.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => openEnquiry("General enquiry")} data-testid="nav-enquire-btn"
            className="btn-gold !min-h-[40px] whitespace-nowrap !px-4 !py-2.5 sm:!px-6 !rounded-full"><span className="sm:hidden">Enquire</span><span className="hidden sm:inline">Enquire now</span></button>
          <button type="button" className="grid h-11 w-11 place-items-center text-[#F8F3E9] xl:hidden" onClick={() => setOpen((o) => !o)}
            aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" strokeWidth={1.4} /> : <Menu className="h-6 w-6" strokeWidth={1.4} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav id="mobile-menu" aria-label="Mobile" data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "100svh" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 top-0 -z-10 overflow-hidden bg-[#21170F] xl:hidden">
            <ul className="flex h-full flex-col justify-center gap-1 px-8">
              {NAV.map((n, i) => (
                <motion.li key={n.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.6 }}>
                  <button type="button" onClick={() => go(n.id)} data-testid={`mobile-nav-link-${n.id}`}
                    className={`font-serif-lux py-2 text-4xl ${active === n.id ? "text-[#B18A4A]" : "text-[#F8F3E9]"}`}>{n.label}</button>
                </motion.li>
              ))}
              <li className="mt-10 border-t border-[#B18A4A]/30 pt-6 text-xs uppercase tracking-[0.3em] text-[#E8D6B7]">Crafted in Agra</li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
